import type { TaskNode } from "./types";

export function uid(): string {
  return "t" + Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-4);
}

export function newTask(name?: string, estimate = 1): TaskNode {
  return {
    id: uid(),
    name: name || "New task",
    estimate,
    collapsed: false,
    disabled: false,
    done: false,
    questions: false,
    comment: "",
    tag: "",
    links: [],
    version: "",
    component: "",
    jiraKey: "",
    pinned: false,
    complexity: "none",
    estimates: {},
    children: [],
  };
}

// coerce a stored complexity value to a valid level (unknown/missing → "none")
export function sanitizeComplexity(c: any): TaskNode["complexity"] {
  return c === "easy" || c === "medium" || c === "hard" ? c : "none";
}

// keep only string keys → finite numbers (drops junk / stale non-object)
export function sanitizeEstimates(e: any): Record<string, number> {
  if (!e || typeof e !== "object" || Array.isArray(e)) return {};
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(e)) if (typeof k === "string" && k && Number.isFinite(+(v as any))) out[k] = +(v as any);
  return out;
}

// validate + standardize loaded data; backfills every field (backward compatible)
export function normalize(nodes: any): TaskNode[] {
  if (!Array.isArray(nodes)) return [];
  return nodes.map((n: any) => ({
    id: n.id || uid(),
    name: typeof n.name === "string" ? n.name : "Task",
    estimate: Number.isFinite(+n.estimate) ? +n.estimate : 0,
    collapsed: !!n.collapsed,
    disabled: !!n.disabled,
    done: !!n.done,
    questions: !!n.questions,
    comment: typeof n.comment === "string" ? n.comment : "",
    tag: typeof n.tag === "string" ? n.tag : "",
    links: Array.isArray(n.links) ? n.links.filter((t: any) => typeof t === "string" && t) : [],
    version: typeof n.version === "string" ? n.version : "",
    component: typeof n.component === "string" ? n.component : "",
    jiraKey: typeof n.jiraKey === "string" ? n.jiraKey : "",
    pinned: !!n.pinned,
    complexity: sanitizeComplexity(n.complexity),
    estimates: sanitizeEstimates(n.estimates),
    children: normalize(n.children || []),
  }));
}
